import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Wallet, TrendingDown, TrendingUp, Users, Pencil, Check } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip } from "recharts";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { hasFeature } from "../lib/perms";
import { formatBRL, formatDateBR } from "../lib/format";
import { PageHeader } from "../components/Layout";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const CHART_COLORS = ["#4A5D4E", "#E8B89C", "#8CA899", "#a3785a", "#C76D6D", "#6d8a76"];

const StatCard = ({ icon: Icon, label, value, tone, testid }) => (
    <div className="rounded-2xl border border-border bg-white p-6 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] fade-up" data-testid={testid}>
        <div className={`mb-4 flex h-11 w-11 items-center justify-center rounded-xl ${tone}`}>
            <Icon className="h-5 w-5" />
        </div>
        <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{label}</p>
        <p className="mt-1 font-mono-nums text-2xl font-semibold text-foreground">{value}</p>
    </div>
);

export default function Dashboard() {
    const { user } = useAuth();
    const { data: summary, refetch } = useQuery({ queryKey: ["summary"], queryFn: async () => (await api.get("/finance/summary")).data });
    const { data: collaborators } = useQuery({ queryKey: ["collaborators", "count"], queryFn: async () => (await api.get("/collaborators")).data, enabled: hasFeature(user, "colaboradores") });
    const { data: expenses } = useQuery({ queryKey: ["expenses", "recent"], queryFn: async () => (await api.get("/expenses")).data });

    const [editing, setEditing] = useState(false);
    const [saldoInicial, setSaldoInicial] = useState("");

    useEffect(() => {
        if (summary) setSaldoInicial(String(summary.saldo_inicial));
    }, [summary]);

    const saveBalance = async () => {
        try {
            await api.put("/finance/balance", { saldo_inicial: parseFloat(saldoInicial) || 0 });
            setEditing(false);
            refetch();
            toast.success("Saldo inicial atualizado");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    const chartData = (summary?.por_categoria || []).map((c) => ({ name: c.categoria, valor: c.valor }));
    const recent = (expenses || []).slice(0, 6);

    return (
        <div>
            <PageHeader overline="Visão geral" title="Painel financeiro" description="Acompanhe o saldo, as despesas e a equipe do seu lar em um só lugar." />

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                <div className="rounded-2xl border border-border bg-white p-6 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] fade-up" data-testid="stat-saldo-inicial">
                    <div className="mb-4 flex items-center justify-between">
                        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-muted text-primary">
                            <Wallet className="h-5 w-5" />
                        </div>
                        <button onClick={() => (editing ? saveBalance() : setEditing(true))} data-testid="edit-saldo-btn" className="rounded-lg p-2 text-muted-foreground hover:bg-muted">
                            {editing ? <Check className="h-4 w-4 text-primary" /> : <Pencil className="h-4 w-4" />}
                        </button>
                    </div>
                    <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Saldo inicial</p>
                    {editing ? (
                        <Input data-testid="saldo-inicial-input" type="number" step="0.01" value={saldoInicial} onChange={(e) => setSaldoInicial(e.target.value)} className="mt-1 rounded-xl font-mono-nums" />
                    ) : (
                        <p className="mt-1 font-mono-nums text-2xl font-semibold">{formatBRL(summary?.saldo_inicial)}</p>
                    )}
                </div>
                <StatCard icon={TrendingUp} label="Saldo atual" value={formatBRL(summary?.saldo_atual)} tone="bg-primary/10 text-primary" testid="stat-saldo-atual" />
                <StatCard icon={TrendingDown} label="Total despesas" value={formatBRL(summary?.total_despesas)} tone="bg-expense/10 text-expense" testid="stat-despesas" />
                <StatCard icon={Users} label="Colaboradores" value={collaborators?.length ?? 0} tone="bg-accent/15 text-accent" testid="stat-colaboradores" />
            </div>

            <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
                <div className="rounded-2xl border border-border bg-white p-6 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] lg:col-span-2" data-testid="chart-categorias">
                    <h3 className="font-heading text-xl font-medium">Despesas por categoria</h3>
                    <p className="mb-6 text-sm text-muted-foreground">Distribuição dos gastos registrados</p>
                    {chartData.length === 0 ? (
                        <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">Nenhuma despesa registrada ainda.</div>
                    ) : (
                        <ResponsiveContainer width="100%" height={280}>
                            <BarChart data={chartData} margin={{ left: 10, right: 10 }}>
                                <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#635F59" }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fontSize: 11, fill: "#969085" }} axisLine={false} tickLine={false} width={70} tickFormatter={(v) => `R$${v}`} />
                                <Tooltip formatter={(v) => formatBRL(v)} cursor={{ fill: "rgba(0,0,0,0.03)" }} contentStyle={{ borderRadius: 12, border: "1px solid #E5E0D8" }} />
                                <Bar dataKey="valor" radius={[8, 8, 0, 0]}>
                                    {chartData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </div>

                <div className="rounded-2xl border border-border bg-white p-6 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)]" data-testid="recent-activity">
                    <h3 className="font-heading text-xl font-medium">Lançamentos recentes</h3>
                    <div className="mt-4 space-y-3">
                        {recent.length === 0 && <p className="text-sm text-muted-foreground">Sem lançamentos.</p>}
                        {recent.map((e) => (
                            <div key={e.id} className="flex items-center justify-between border-b border-border/60 pb-3 last:border-0">
                                <div className="min-w-0">
                                    <p className="truncate text-sm font-medium">{e.description}</p>
                                    <p className="text-xs text-muted-foreground">{e.category} · {formatDateBR(e.date)}</p>
                                </div>
                                <span className={`font-mono-nums text-sm font-semibold ${e.tipo === "receita" ? "text-income" : "text-expense"}`}>
                                    {e.tipo === "receita" ? "+" : "-"}{formatBRL(e.amount)}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
